import { useEffect, useState } from 'react';
import { fetchData } from 'services/api/fetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';

// The price information of an orderable module
export type OrderableModule = {
    feature: string;
    price_type: string;
    flat_price: string;
    price_per_seat: string;
};

// The module ordering data of the environment, active features are turned on, available features also include turned off ones
export type ModuleFeaturesData = {
    availableFeatures: string[];
    activeFeatures: string[];
    reactivatableFeatures: string[];
    orderableFeatures: string[];
    orderableModules: OrderableModule[];
    unreleasedFeatures: string[];
};

// Cache the module features request per environment to prevent duplicate requests
let cachedEnvironmentHash: string | null = null;
let cachedRequest: Promise<ModuleFeaturesData> | null = null;

// Clears the cached module features so the next mount refetches them
export const clearModuleFeaturesCache = () => {
    cachedEnvironmentHash = null;
    cachedRequest = null;
};

// Returns the available, orderable and unreleased module features of the environment
export const useOrderableModuleFeatures = (): ModuleFeaturesData | null => {
    const { environmentHash, handleLogout } = useAuthContext();
    const [moduleFeatures, setModuleFeatures] = useState<ModuleFeaturesData | null>(null);

    // Fetch the module features once per environment
    useEffect(() => {

        // Start a new request when the cache is empty or belongs to another environment
        if (!cachedRequest || cachedEnvironmentHash !== environmentHash) {
            cachedEnvironmentHash = environmentHash;
            cachedRequest = fetchData({ apiUrl: 'get_orderable_module_features', handleLogout })
                .then(response => ({
                    availableFeatures: response?.available_features ?? [],
                    activeFeatures: response?.active_features ?? [],
                    reactivatableFeatures: response?.reactivatable_features ?? [],
                    orderableFeatures: response?.orderable_features ?? [],
                    orderableModules: response?.orderable_modules ?? [],
                    unreleasedFeatures: response?.unreleased_features ?? []
                }));
        }

        // Set the fetched features in the state while the component is mounted
        let isMounted = true;
        cachedRequest.then(features => { if (isMounted) setModuleFeatures(features); });
        return () => { isMounted = false; };
    }, [environmentHash]);

    return moduleFeatures;
};
